import React, { type RefObject } from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import styles from './ArtifactPanel.module.css';
import { AhaCopyButton } from 'components/common/reactifyWebComponent';
import {
  type AssistantArtifact,
  type AssistantToolCall,
  Page,
} from 'components/framework/models';
import { allowReactContentPreview } from '../utils/allowReactContentPreview';
import { getImageClipboardItem } from '../utils/copyImageToClipboard';
import { PanelCloseIcon } from './PanelCloseIcon';
import ShareArtifactPopover from '../SharePopover/ShareArtifactPopover';
import { FROZEN_CHAT_ERROR } from 'components/Assistant/constants';
import Tool from '../../agent_tools/Tool';
import { renderCitesAsMarkdownLinks } from '../ConversationPanel/MarkdownContent';
import { type ContentRef } from '../ConversationPanel/ContentRef';

export const CONTENT_TYPE_SUPPORTS_SOURCE = [
  'text/html',
  'text/javascript',
  'text/typescript',
  'application/vnd.aha.react',
  'application/vnd.aha.react_entrypoint',
];

const ArtifactHeader = ({
  toolCall,
  artifact,
  contentRef,
}: {
  toolCall: AssistantToolCall | null;
  artifact: AssistantArtifact | null;
  contentRef: RefObject<ContentRef>;
}) => {
  const controller = useController<AssistantViewController>();
  const { artifactDisplayMode, size, readOnly } = controller.state;

  const isDevelopment = ['development', 'staging'].includes(
    process.env.NODE_ENV || 'unknown'
  );

  const modeButtons = CONTENT_TYPE_SUPPORTS_SOURCE.includes(
    artifact?.contentType || 'text/markdown'
  ) &&
    toolCall &&
    allowReactContentPreview(toolCall, artifact) && (
      <aha-button-group>
        <aha-button
          size='small'
          active={artifactDisplayMode === 'preview' ? true : undefined}
          onClick={() => controller.actionSetArtfactDisplayMode('preview')}
        >
          <i className='far fa-eye' /> Preview
        </aha-button>
        <aha-button
          size='small'
          active={artifactDisplayMode === 'source' ? true : undefined}
          onClick={() => controller.actionSetArtfactDisplayMode('source')}
        >
          <i className='far fa-code' /> Source
        </aha-button>
      </aha-button-group>
    );

  const approvalButtons = !readOnly && toolCall && (
    <>
      {toolCall.status === 'needs_approval' && (
        <>
          <aha-button
            kind='default'
            size='small'
            {...(controller.frozenOrNoUsageAvailable()
              ? { disabled: true }
              : {})}
            onClick={() => controller.actionRejectToolCall(toolCall)}
          >
            {toolCall.content?.rejectButton?.label || 'Ignore'}
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
          <aha-button
            kind='primary'
            size='small'
            {...(controller.frozenOrNoUsageAvailable()
              ? { disabled: true }
              : {})}
            onClick={() => controller.actionApproveToolCall(toolCall)}
          >
            {toolCall.content?.actionButton?.label || 'Approve'}
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
        </>
      )}
      {isDevelopment && toolCall.status === 'error' && (
        <>
          <aha-button
            kind='primary'
            size='mini'
            {...(controller.frozenOrNoUsageAvailable()
              ? { disabled: true }
              : {})}
            onClick={() => controller.actionApproveToolCall(toolCall)}
          >
            Try again
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
        </>
      )}
    </>
  );

  const openReportButton = artifact?.contentType ===
    'application/vnd.aha.bookmark' && (
    <aha-button
      kind='gray'
      size='mini'
      onClick={() => {
        window.open(
          `/bookmarks/${toolCall!.content!.panel!.body.replace(/[^0-9]/g, '')}`,
          '_blank'
        );
      }}
    >
      View report
    </aha-button>
  );

  const artifactData =
    toolCall &&
    (JSON.parse(toolCall.content?.data || '{}') as Record<string, any>);

  const openNoteButton =
    toolCall &&
    artifactData!.page_id &&
    (artifactData!.page_id === 'loading' ? (
      <aha-button kind='icon' size='mini'>
        <i className='fa fa-regular fa-circle-notch fa-spin' />
      </aha-button>
    ) : (
      <aha-button
        kind='icon'
        size='mini'
        onClick={() => {
          const pageId = artifactData!.page_id;
          const path = Page.pathForId(pageId);
          if (path) {
            window.open(path, '_blank');
          }
        }}
      >
        <i className='far fa-arrow-up-right-from-square' />
        <aha-tooltip style={{ '--Tooltip__tooltip--min-width': 0 }}>
          Open note in new tab
        </aha-tooltip>
      </aha-button>
    ));

  let agentButtons: React.ReactNode | null = null;
  if (
    toolCall &&
    !readOnly &&
    toolCall.status !== 'needs_approval' &&
    toolCall.status !== 'error'
  ) {
    agentButtons = Tool.getToolActions(toolCall, controller.agent);
  }

  let toolCallData: Record<string, any> | null = null;
  if (toolCall?.content?.data) {
    toolCallData = JSON.parse(toolCall.content?.data) as Record<string, any>;
  }

  const isImagePanel = artifact?.contentType === 'application/vnd.aha.image';

  const copyButton = toolCall &&
    toolCall.content?.panel?.body &&
    toolCall.status === 'complete' && (
      <>
        <AhaCopyButton
          size='mini'
          {...(isImagePanel
            ? {
                getItem: async () =>
                  getImageClipboardItem({
                    imageUrl: toolCall.content!.panel!.body,
                  }),
              }
            : {
                getText: () => {
                  if (contentRef.current?.getCopyableContent) {
                    return contentRef.current.getCopyableContent();
                  } else if (
                    toolCall.content?.panel?.contentType === 'text/markdown'
                  ) {
                    return renderCitesAsMarkdownLinks(
                      toolCall.content.panel.body
                    );
                  } else if (
                    toolCall.content?.panel?.contentType ===
                      'application/vnd.aha.react_entrypoint' &&
                    toolCall.artifact
                  ) {
                    // Copy the share link to the artifact instead of the code as thats what user likely wants to share.
                    const shareUrl = `/assistant/shared_artifacts/${
                      toolCall.artifact.token
                    }/${toolCallData?.version || 1}`;
                    return `${window.location.origin}${shareUrl}`;
                  } else {
                    return toolCall.content?.panel?.body;
                  }
                },
              })}
        />
        <aha-tooltip style={{ '--Tooltip__tooltip--min-width': 0 }}>
          {isImagePanel ? 'Copy image to clipboard' : 'Copy to clipboard'}
        </aha-tooltip>
      </>
    );

  const hasAppTsx =
    artifact?.contentType !== 'application/vnd.aha.react_entrypoint' ||
    toolCallData?.has_app_file === true;

  const shareArtifact = !readOnly && toolCall?.artifact && hasAppTsx && (
    <ShareArtifactPopover
      artifact={toolCall.artifact}
      version={toolCallData?.version}
    />
  );

  return (
    <div className={styles.artifactHeader}>
      <div className={styles.artifactLeftControls}>
        <aha-button
          kind='icon'
          size='mini'
          onClick={() => controller.actionCloseArtifact()}
        >
          {['full-screen', 'full-page'].includes(size) ? (
            <PanelCloseIcon />
          ) : (
            <i className='far fa-xmark' />
          )}
        </aha-button>
        {modeButtons}
        {toolCall?.status === 'preparing' && <aha-loading-dots />}
        <div className={styles.artifactName}>{artifact?.title}</div>
      </div>
      <div className={styles.artifactTools}>
        {approvalButtons}
        {agentButtons}
        {openReportButton}
        {openNoteButton}
        {!openReportButton && copyButton}
        {shareArtifact}
      </div>
    </div>
  );
};

export default ApplicationView(ArtifactHeader);
